import {FilePicker, FormField, FormFieldValidationMessage, FormFieldValidationMessageProps, Pane} from "evergreen-ui";
import React, {useState} from "react";
import {ProviderUserImportFormInput} from "./ProviderUserImportPopup";
import {EMAIL_VALIDATION_REGEX} from "common/util/validations";
import {ClientSidePagination} from "./ClientSidePagination";
import {get} from "lodash";
import {Paths} from "common";
import {Controller, FieldErrors, useFormContext} from "react-hook-form";
import {Observable} from "rxjs";
import {useObservable} from "rxsupport";
import {ProviderUserImportInput} from "usermanagementapp/domain/types";
import {FeatureFlaggable, ProviderPortalFeatureFlag, useFeatureFlag, useFeatureFlagFilter} from "featureflag";

const emailRegExp = new RegExp(EMAIL_VALIDATION_REGEX);

interface ImportUserInputProps {
    isSubmitting$: Observable<boolean>
}

export const CSVImportUserInput = ({isSubmitting$}: ImportUserInputProps) => {
    const [rows, setRows] = useState<ProviderUserImportInput[]>([]);
    const [invalidCsv, setInvalidCsv] = useState(false);
    const fileReader = new FileReader();
    const sending = useObservable(isSubmitting$, false);
    // Both flag names read backwards: true means the feature is switched off.
    const exportEnabled = !useFeatureFlag(ProviderPortalFeatureFlag.TDTL_9900_IS_EXPORT_DISABLED);
    const contactEnabled = !useFeatureFlag(ProviderPortalFeatureFlag.BHPNPP_3240_IS_BIZ_CONTACT_DISABLED);
    // One list feeds both the parser and the template, so the two cannot disagree on the file shape.
    const trailing = trailingImportColumns({exportEnabled, contactEnabled});

    const {
        control,
        setValue,
        formState: {
            errors
        }
    } = useFormContext<ProviderUserImportFormInput>();

    const handleOnChange = (files: FileList) => {
        if (files && files.length > 0) {
            const file = files[0]
            fileReader.onload = function (event) {
                const text = event?.target?.result;
                if (text != null) {
                    csvFileToArray(text as string);
                }
            };

            fileReader.readAsText(file);
        }
    }
    const csvFileToArray = (csvFile: string) => {
        const csvRows = csvFile.slice(csvFile.indexOf("\n") + 1).split("\n");
        let hasInvalid = false;
        let processedImport: ProviderUserImportInput[] = []
        csvRows.forEach((csvRow) => {
            const values = csvRow.split(",");
            const {row, isValid} = extractImportData(values, trailing);
            if (row != null) {
                processedImport.push(row);
            }
            if (!isValid && csvRow !== "") {
                hasInvalid = true;
            }
        })

        setInvalidCsv(hasInvalid);

        if (processedImport.length === 0) {
            setInvalidCsv(true);
        }


        setRows(processedImport);
        setValue('imports', processedImport)
    };

    const validationRules = {
        required: true,
        validate: () => {
            return !invalidCsv;
        }
    };

    // @ts-ignore
    return (
        <Controller
            control={control}
            rules={validationRules}
            render={
                () =>
                    <FormField
                        label={<span className='text-base font-normal'>User List</span>}
                        validationMessage={<ImportErrorMessage errors={errors}/>}
                        labelFor={"imports"}
                    >
                        <FilePicker width={250}
                                    marginTop={'12px'}
                                    disabled={sending}
                                    accept={".csv"}
                                    multiple={false}
                                    className={"ev-overwrite ev-file-input-hidden"}
                                    paddingBottom={"4px"}
                                    height={36}
                                    onClick={(e: any) => {
                                        if (e.target.type !== "text") {
                                            e.target.value = null;
                                        }

                                        return false;
                                    }}
                                    onChange={handleOnChange}
                        />
                        <div className="text-sm">
                            Note: Csv file must following this
                            <span onClick={() => downloadTemplate(trailing)}
                                  className="pl-1 text-blue-portal-link underline cursor-pointer">
                                template
                            </span>
                        </div>
                        {
                            invalidCsv &&
                            <div className="font-bold text-red-600 pb-2">
                    <span>
                        Some rows of an imported file are invalid
                    </span>
                            </div>
                        }
                        <CsvImportPreviewTable rows={rows} perPage={5}/>
                    </FormField>
            } name={'imports'}/>
    );
}

type ImportErrorMessageProps = {
    errors?: FieldErrors;
} & FormFieldValidationMessageProps;
const ImportErrorMessage = ({errors, ...rest}: ImportErrorMessageProps) => {
    return (!!errors?.imports) ?
        <FormFieldValidationMessage marginTop={8} {...rest}>
            {errors.imports && errors.imports.type === 'validate' && (
                <Pane is='span' color='#ec4c47'>Need to correct import file before submitting</Pane>
            )}
            {errors.imports && errors.imports.type === 'required' && (
                <Pane is='span' color='#ec4c47'>This field is required.</Pane>
            )}
        </FormFieldValidationMessage> : null ?? null;
}

/** The grant columns that follow the eight fixed ones. Each exists only while its feature is on. */
export type TrailingImportColumn = 'auditLogAccess' | 'contactEditAccess' | 'contactReadAccess';

/** The eight columns every file carries, before any flag-dependent grant column. */
const FIXED_COLUMN_COUNT = 8;

/**
 * The trailing column order lives here and nowhere else. Audit Log first, then Contact Management
 * Edit, then Contact Management.
 * The parser reads by position and the template writes by position, so both take this list.
 */
export const trailingImportColumns = ({exportEnabled, contactEnabled}: {exportEnabled: boolean; contactEnabled: boolean}): TrailingImportColumn[] => {
    const trailing: TrailingImportColumn[] = [];
    if (exportEnabled) {
        trailing.push('auditLogAccess');
    }
    if (contactEnabled) {
        trailing.push('contactEditAccess');
        trailing.push('contactReadAccess');
    }
    return trailing;
};

/**
 * Parses one CSV row by column position. The width is the eight fixed columns plus one per
 * trailing grant column, so it follows the flags.
 *
 * A row of the wrong width is rejected outright rather than partly read. A flag can flip between
 * an admin downloading the template and uploading the filled file, and reading a file one column
 * short would silently drop or misplace a grant.
 */
export const extractImportData = (values: string[], trailing: TrailingImportColumn[]) => {
    if (values.length !== FIXED_COLUMN_COUNT + trailing.length) {
        return {
            row: null,
            isValid: false
        };
    }

    // A grant whose column is absent from the file is withheld. Contact Management is the exception below.
    const trailingGrant = (column: TrailingImportColumn) => {
        const position = trailing.indexOf(column);
        return position >= 0 && values[FIXED_COLUMN_COUNT + position].toLowerCase().includes('yes');
    };

    const data = {
        email: values[0],
        isEmailValid: isEmailValid(values[0]),
        firstName: values[1],
        isFirstNameValid: isFirstNameValid(values[1]),
        lastName: values[2],
        isLastNameValid: isLastNameValid(values[2]),
        referralManagementAccess: values[3].toLowerCase().includes('yes'),
        clientCareAccess: values[4].toLowerCase().includes('yes'),
        providerMetricsAccess: values[5].toLowerCase().includes('yes'),
        rosterAdministrationAccess: values[6].toLowerCase().includes('yes'),
        userManagementAccess: values[7].toLowerCase().includes('yes'),
        auditLogAccess: trailingGrant('auditLogAccess'),
        contactEditAccess: trailingGrant('contactEditAccess'),
        // Edit needs read, the rule the user dialog keeps, so a Yes under edit grants read too.
        // With the contact feature off the file has no read column. Every row gets read then,
        // the way the hidden box in the dialog posts true.
        contactReadAccess: trailing.includes('contactReadAccess')
            ? trailingGrant('contactEditAccess') || trailingGrant('contactReadAccess')
            : true,
    }
    return {
        row: data,
        isValid: data.isEmailValid && data.isFirstNameValid && data.isLastNameValid
    }
}
const isEmailValid = (email: string) => {
    return emailRegExp.test(email)
}

const isFirstNameValid = (firstName: string) => {
    return firstName.length >= 1 && firstName.length <= 50;
}

const isLastNameValid = (lastName: string) => {
    return lastName.length >= 1 && lastName.length <= 50;
}
type CsvImportPreviewTableProps = {
    rows: ProviderUserImportInput[] | undefined;
    perPage?: number
}

const CsvImportPreviewTable = ({rows, perPage = 5}: CsvImportPreviewTableProps) => {
    const [page, setPage] = useState(1);
    const columns = useFeatureFlagFilter(ProviderUserImportInputListColumn);
    if (!rows || rows.length === 0) {
        return <div/>
    }

    let displayRows = rows.slice((page - 1) * perPage, page * perPage)

    return (
        <div>
            <div className="flex space-between justify-end pb-4">
                <ClientSidePagination total={rows.length}
                                      limit={perPage}
                                      page={page}
                                      changePage={page => setPage(page)}/>
            </div>
            <table className="table-fixed w-full">
                <thead>
                <tr className='text-left select-none text-sm text-gray-portal-table-title'>
                    {
                        columns.map((column) => (
                            <th
                                key={column.attr}
                                className={'font-light' + (column.attr === 'email' ? ' pl-8' : '')}
                            >
                                <div className='flex items-center'>
                                    <div>
                                        {column.name}
                                    </div>
                                </div>
                            </th>
                        ))
                    }
                </tr>
                </thead>
                <tbody>
                {
                    displayRows.length !== 0 && displayRows.map((row) => (
                        <ProviderUserImportInputPreview key={row.email} row={row} columns={columns}/>
                    ))
                }
                </tbody>
            </table>
        </div>

    )
}

type ProviderUserImportInputColumn = FeatureFlaggable<{
    name: string;
    type: "text" | "boolean"
    attr: Paths<ProviderUserImportInput>;
}>;

export const ProviderUserImportInputListColumn: Readonly<ProviderUserImportInputColumn[]> = [
    {name: 'Email', attr: 'email', type: "text"},
    {name: 'First Name', attr: 'firstName', type: "text"},
    {name: 'Last Name', attr: 'lastName', type: "text"},
    {name: 'Client Placement Access', attr: 'referralManagementAccess', type: "boolean"},
    {name: 'Client Care Access', attr: 'clientCareAccess', type: "boolean"},
    {name: 'Provider Metrics Access', attr: 'providerMetricsAccess', type: "boolean"},
    {name: 'Roster Management Access', attr: 'rosterAdministrationAccess', type: "boolean"},
    {name: 'User Management Access', attr: 'userManagementAccess', type: 'boolean'},
    // The trailing columns, in the order trailingImportColumns reads them from the file.
    // Both flag names read backwards, hence negate.
    {
        name: 'Audit Log Access', attr: 'auditLogAccess', type: 'boolean',
        featureFlag: ProviderPortalFeatureFlag.TDTL_9900_IS_EXPORT_DISABLED,
        featureFlagNegate: true,
    },
    {
        name: 'Contact Management Edit Access', attr: 'contactEditAccess', type: 'boolean',
        featureFlag: ProviderPortalFeatureFlag.BHPNPP_3240_IS_BIZ_CONTACT_DISABLED,
        featureFlagNegate: true,
    },
    {
        name: 'Contact Management Access', attr: 'contactReadAccess', type: 'boolean',
        featureFlag: ProviderPortalFeatureFlag.BHPNPP_3240_IS_BIZ_CONTACT_DISABLED,
        featureFlagNegate: true,
    },
]


type ProviderUserImportInputPreviewProps = {
    row: ProviderUserImportInput;
    columns: Readonly<ProviderUserImportInputColumn[]>;
}

const ProviderUserImportInputPreview = ({row, columns}: ProviderUserImportInputPreviewProps) => {
    const displayColumn = (column: ProviderUserImportInputColumn) => {
        if (column.type === "boolean") {
            return <>
                {get(row, column.attr) ? 'Yes' : 'No'}
            </>
        }

        if (column.attr === "email") {
            return <span className={`${!row.isEmailValid ? 'text-red-600' : ''}`}>
                   {
                       row.email
                   }
            </span>
        }

        if (column.attr === "firstName") {
            return <span className={`${!row.isFirstNameValid ? 'text-red-600' : ''}`}>
                   {
                       row.firstName
                   }
            </span>
        }

        if (column.attr === "lastName") {
            return <span className={`${!row.isLastNameValid ? 'text-red-600' : ''}`}>
                   {
                       row.lastName
                   }
            </span>
        }

        return <>{get(row, column.attr)}</>
    }
    const allValid = row.isLastNameValid && row.isFirstNameValid && row.isEmailValid;

    return (
        <tr className={`border-b border-gray-200 relative hover:${!allValid ? "bg-red-300" : "bg-gray-200"} ${!allValid ? "bg-red-200" : ""}`}>
            {
                columns.map((column) => (
                    <td
                        key={column.name}
                        className={'py-5 truncate' + (column.attr === 'email' ? ' pl-8' : '')}
                    >
                        {
                            displayColumn(column)
                        }
                    </td>
                ))
            }
        </tr>
    );
}

const TRAILING_COLUMN_HEADERS: Record<TrailingImportColumn, string> = {
    auditLogAccess: "Audit Log",
    contactEditAccess: "Contact Management Edit",
    contactReadAccess: "Contact Management",
};

/**
 * The template's two rows, header then sample. Width follows the trailing list, the way the parser does.
 * The sample grants Contact Management, since every other way of creating a user grants it by default.
 */
export const templateRows = (trailing: TrailingImportColumn[]) => [
    ["Email", "First Name", "Last Name", "Client Placement", "Client Care", "Provider Metrics", "Roster Management", "User Administration",
        ...trailing.map((column) => TRAILING_COLUMN_HEADERS[column])],
    ["sample@email.com", "first name", "last name", "Yes", "Yes", "No", "No", "No",
        ...trailing.map((column) => column === 'contactReadAccess' ? "Yes" : "No")]
];

const downloadTemplate = (trailing: TrailingImportColumn[]) => {
    const rows = templateRows(trailing);
    let csvContent = "data:text/csv;charset=utf-8," + rows.map((e) => e.join(",")).join("\n");
    let encodedUri = encodeURI(csvContent);
    let link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "template.csv");
    document.body.appendChild(link);

    link.click();
}
